import React, {useState, useEffect, useMemo} from 'react';
import { AgGridReact } from "ag-grid-react";
import { TileLayout } from '@progress/kendo-react-all';

const PerfAndStatsGrid = ({gridData, weightedIndex ,portfolio,index}) => {
    const [monthlyPerfData, setMonthlyPerfData] = useState([]);
    const [historicalData, setHistorialData] = useState([]);
    const [riskFactorData, setRiskFactorData] = useState([]);
    const [bm1Name, setBm1Name] = useState("BM1");
    const [bm2Name, setBm2Name] = useState("BM2");


    const [wghtIndex,setWghtIndex]=useState(weightedIndex);
    useEffect(()=>{
      setWghtIndex(val=>weightedIndex)
    },[weightedIndex])
    

    const transformMtdReturns = (MtdReturns) => {
        const monthNames = [
          'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];
      
        const dataByYear = {};
      
        MtdReturns.forEach(entry => {
          const date = new Date(entry.ReturnDate);
          const year = date.getFullYear();
          const month = date.getMonth(); // 0-indexed
          const ror = entry.RoR;
      
          if (!dataByYear[year]) {
            dataByYear[year] = { Year: year, YearTotalRaw: 0 };
            monthNames.forEach(m => dataByYear[year][m] = ""); // initialize months
          }
      
          if (ror != null) {
            dataByYear[year][monthNames[month]] = (ror * 100).toFixed(1) + "%";
            dataByYear[year].YearTotalRaw += ror; // accumulate raw RoR
          }
        });
      
        // Final formatting and cleanup
        const result = Object.values(dataByYear).map(row => {
          return {
            ...row,
            YearTotal: (row.YearTotalRaw * 100).toFixed(1) + "%",
          };
        });
      
        // Sort descending by year
        return result.sort((a, b) => b.Year - a.Year);
    };

    const convertStatisticsData = (statisticsData) => {
      const catStats = statisticsData.CatStatistics;
    
      const getStatValue = (statsArray, statName) => {
        const stat = statsArray.find(s => s.StatName === statName);
        return stat ? stat.StatResult : null;
      };
    
      const toPercent = num =>
        typeof num === 'number' ? `${(num * 100).toFixed(1)}%` : "N/A";
    
      const toCash = num =>
        typeof num === 'number' ? `$${Math.round(num).toLocaleString()}` : "N/A";
    
      const toRaw = num =>
        typeof num === 'number' ? num.toFixed(1).toString() : "N/A";
    
      const formatValue = (value, descriptor) => {
        if (descriptor === "Cumulative VAMI") {
          return toCash(value);
        } else if (
          descriptor.includes("Sortino") ||
          descriptor.includes("Correlation") ||
          descriptor.includes("Months in Maximum Drawdown")
        ) {
          return toRaw(value);
        } else {
          return toPercent(value);
        }
      };
    
      const portfolioStats = catStats.find(item=>item.CatName=="Portfolio")?.Statistics || [];
      const bm1Stats = catStats.find(item=>item.CatName==index?.selectedBM1?.MarketIndexName)?.Statistics || [];
      const bm2Stats = catStats.find(item=>item.CatName==index?.selectedBM2?.MarketIndexName)?.Statistics || [];
      const wiStats = catStats.find(item=>item.CatName=="Weighted Index")?.Statistics || [];
    
      setBm1Name(catStats[1].CatName);
      setBm2Name(catStats[2].CatName);
    
      const rowData = portfolioStats.map(portfolioStat => {
        const statName = portfolioStat.StatName;
        const statDescriptor = portfolioStat.StatDescriptor;
    
        const portfolioResult = formatValue(portfolioStat.StatResult, statDescriptor);
        const bm1Result = formatValue(getStatValue(bm1Stats, statName), statDescriptor);
        const bm2Result = formatValue(getStatValue(bm2Stats, statName), statDescriptor);
        const WeightedIndex = formatValue(getStatValue(wiStats, statName), statDescriptor);
    
        return {
          statDescriptor,
          portfolioResult,
          bm1Result,
          bm2Result,
          WeightedIndex
        };
      });
      setHistorialData(rowData.slice(0, 6));
      setRiskFactorData(rowData.slice(6));
    };    
      
    useEffect(()=>{
        if(gridData){
            if(gridData?.MonthlyPerformance.length >0){
                const performanceBlockData = 
                // weightedIndex ? 
                // gridData?.MonthlyPerformance.find((item)=>item.StrategyName === "Weighted Index") :
                gridData?.MonthlyPerformance.find((item)=>item.StrategyName === "Total Portfolio")

                const statisticsBlockData = 
                // weightedIndex ?
                // gridData?.StatisticsSection.find((item)=> item.StrategyName === "Weighted Index") :
                gridData?.StatisticsSection.find((item)=> item.StrategyName === "Total Portfolio")

                setMonthlyPerfData(transformMtdReturns(performanceBlockData?.MtdReturns))

                convertStatisticsData(statisticsBlockData);
            }
            else{
                setMonthlyPerfData([]);
                setHistorialData([]);
                setRiskFactorData([]);
            }
        }
    },[gridData, weightedIndex,portfolio]);
    const monthlyPerfColumnDefs = useMemo(()=>[
      { headerName: 'Year', field: 'Year', pinned: 'left', width: 100 },
      ...[
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ].map(month => ({
        headerName: month,
        field: month,
        cellStyle: params => {
          const value = params.value;
          if (!value) return null;
          if (value.startsWith('-')) {
            return { color: 'red' };
          }
          return { color: 'black' };
        },
        width: 80,
        type: 'numericColumn',
        cellClass: 'ag-right-aligned-cell',
      })),
      { 
          headerName: 'Year Total', 
          field: 'YearTotal', 
          pinned: 'right', 
          width: 100, 
          cellStyle: params => {
          const value = params.value;
          if (!value) return null;
          if (value.startsWith('-')) {
            return { color: 'red' };
          }
          return { color: 'black' };
        },},
    ],[monthlyPerfData]) ;

    const historicalDataColDef = useMemo( ()=>[
      {headerName:"Historical Data", field:"statDescriptor", flex:0.4},
      {headerName:"Portfolio", field:"portfolioResult",flex:0.3,cellStyle: params => {
        const value = params.value;
        if (!value) return null;
        if (value.startsWith('-')) {
          return { color: 'red', textAlign:'right' };
        }
        return { color: 'black', textAlign:'right' };
      },},
      {headerName:`${index?.selectedBM1?.MarketIndexName}`, field:"bm1Result",flex:0.3,cellStyle: params => {
        const value = params.value;
        if (!value) return null;
        if (value.startsWith('-')) {
          return { color: 'red', textAlign:'right' };
        }
        return { color: 'black', textAlign:'right' };
      },},
      {headerName:`${index?.selectedBM2?.MarketIndexName}`, field:"bm2Result",flex:0.3,cellStyle: params => {
        const value = params.value;
        if (!value) return null;
        if (value.startsWith('-')) {
          return { color: 'red', textAlign:'right' };
        }
        return { color: 'black', textAlign:'right' };
      },
      hide: wghtIndex,
      },
      {headerName:`Weighted Index`, field:"WeightedIndex",flex:0.3,
      cellStyle: params => {
        const value = params.value;
        if (!value) return null;
        if (value.startsWith('-')) {
          return { color: 'red', textAlign:'right' };
        }
        return { color: 'black', textAlign:'right' };
      },
      hide: !wghtIndex,
    }
    ],[historicalData])
   
    const riskFactorColDef = [
      {headerName:"Risk Factor", field:"statDescriptor", flex:0.4},
      {headerName:"Portfolio", field:"portfolioResult",flex:0.3,cellStyle: params => {
        const value = params.value;
        if (!value) return null;
        if (value.startsWith('-')) {
          return { color: 'red', textAlign:'right' };
        }
        return { color: 'black', textAlign:'right' };
      },},
      {headerName:`${index?.selectedBM1?.MarketIndexName}`, field:"bm1Result",flex:0.3,cellStyle: params => {
        const value = params.value;
        if (!value) return null;
        if (value.startsWith('-')) {
          return { color: 'red', textAlign:'right' };
        }
        return { color: 'black', textAlign:'right' };
      },},
      {headerName:`${index?.selectedBM2?.MarketIndexName}`, field:"bm2Result",flex:0.3,cellStyle: params => {
        const value = params.value;
        if (!value) return null;
        if (value.startsWith('-')) {
          return { color: 'red', textAlign:'right' };
        }
        return { color: 'black', textAlign:'right' };
      },
    
      hide: wghtIndex,
    },
    {headerName:`Weighted Index`, field:"WeightedIndex",flex:0.3,
    cellStyle: params => {
      const value = params.value;
      if (!value) return null;
      if (value.startsWith('-')) {
        return { color: 'red', textAlign:'right' };
      }
      return { color: 'black', textAlign:'right' };
    },
    hide: !wghtIndex,
    }
    ];
    const tiles = [
      {
        header: <div className="header-container d-flex align-items-center justify-content-between" style={{ height: "20px" }}>
        <b className="text-nowrap">Historical Data</b>
    </div>,
        body: <><div
        className="ag-theme-alpine"
        style={{ height: "300px", width: "100%", paddingTop: 0 }}
      >
        <AgGridReact
          rowData={historicalData}
          columnDefs={historicalDataColDef}
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: true,
            flex: 1,
          }}
        />
      </div></>,
        resizable: false,
        componentID:'C1'
    },
    {
      header: <div className="header-container d-flex align-items-center justify-content-between" style={{ height: "20px" }}>
      <b className="text-nowrap">Risk Factor Data</b>
  </div>,
      body: <><div
      className="ag-theme-alpine"
      style={{ height: "300px", width: "100%", paddingTop: 0 }}
    >
      <AgGridReact
        rowData={riskFactorData}
        columnDefs={riskFactorColDef}
        defaultColDef={{
          resizable: true,
          sortable: true,
          filter: true,
          flex: 1,
        }}
      />
    </div></>,
      resizable: false,
      componentID:'C2'
  },
    ]
    const tilePositions = [
      { col: 1, colSpan: 1, rowSpan: 2 },
      { col: 2, colSpan: 1, rowSpan: 2 },
    ]

    return(
        <>
        <div className='row'>
        <div id="assettile">
            <TileLayout
            className="bg-white"
            columns={2}
            rowHeight={175}
            positions={tilePositions}
            gap={{ rows: 10, columns: 10 }}
            items={tiles}
            />
        </div>
        </div>
        <div className='row mt-2'>
        <div
              className="ag-theme-alpine"
              style={{ height: "400px", width: "100%", paddingTop: 0 }}
            >
              <AgGridReact
                rowData={monthlyPerfData}
                columnDefs={monthlyPerfColumnDefs}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                  filter: true,
                  flex: 1,
                }}
              />
            </div>
        </div>
        </>
    )
};

export default PerfAndStatsGrid;